/**
 * The per-supplier daily send budget for the site in view.
 *
 * Its own hook rather than more state on `useNotificationSetup`: this is a
 * separate resource on a separate endpoint, and the setup hook is already at
 * the file-size ceiling.
 *
 * The endpoint returns **every** active relationship, not only the ones
 * carrying an exception, so one read fills both the picker and the exception
 * list. A row with `maxEmailsPerDay === 0` has no exception and follows the
 * site default.
 *
 * Writes apply immediately, like the master switches and quiet hours — there is
 * no staged/save model here. A budget is one number about one supplier;
 * batching that behind a Save would only create a second dirty state for the
 * unsaved-changes guard to reason about.
 *
 * @link ./NotificationSendBudgetCard for the screen.
 */
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { notificationsAPI, type SendBudgetResponse } from '../../../../lib/api';
import { getErrorMessage } from '../../../../hooks/useFetchData';
import type { SupplierSendBudget, WithheldSend } from './types';

export interface UseSendBudgetsResult {
  loading: boolean;
  error: string | null;
  saving: boolean;
  /** Every active supplier for this site, ordered by name. */
  suppliers: SupplierSendBudget[];
  /** What the limits actually held back lately. Empty is the common case. */
  withheld: WithheldSend[];
  reload: () => Promise<void>;
  /** Set one supplier's allowance. `0` clears it back to the default. */
  save: (supplier: number, maxEmailsPerDay: number) => Promise<void>;
}

const readSuppliers = (data: SendBudgetResponse): SupplierSendBudget[] =>
  (data.suppliers ?? []).map((row) => ({
    supplier: row.supplier,
    supplierName: row.supplier_name,
    maxEmailsPerDay: row.max_emails_per_day ?? 0,
  }));

const readWithheld = (data: SendBudgetResponse): WithheldSend[] =>
  (data.withheld ?? []).map((row) => ({
    supplier: row.supplier,
    supplierName: row.supplier_name,
    sends: row.sends ?? 0,
    recipients: row.recipients ?? 0,
    lastDate: row.last_date ?? '',
    cap: row.cap ?? 0,
  }));

/**
 * @param company - the company the screen believes it is configuring, echoed on
 *   every write so a re-scoped screen gets a 409 instead of writing to the
 *   wrong tenant.
 * @returns the supplier list and the one mutation the budget card needs.
 */
export function useSendBudgets(company?: number): UseSendBudgetsResult {
  const [suppliers, setSuppliers] = useState<SupplierSendBudget[]>([]);
  const [withheld, setWithheld] = useState<WithheldSend[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await notificationsAPI.getSendBudgets();
      setSuppliers(readSuppliers(data));
      setWithheld(readWithheld(data));
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Failed to load the send budgets'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const save = useCallback(
    async (supplier: number, maxEmailsPerDay: number) => {
      setSaving(true);
      try {
        const { data } = await notificationsAPI.setSendBudget(
          supplier,
          maxEmailsPerDay,
          company,
        );
        setSuppliers(readSuppliers(data));
        setWithheld(readWithheld(data));
        toast.success(
          maxEmailsPerDay > 0
            ? `Limit set to ${maxEmailsPerDay} a day`
            : 'Limit removed — this supplier follows the default',
        );
      } catch (err: unknown) {
        toast.error(getErrorMessage(err, 'Failed to save the send budget'));
      } finally {
        setSaving(false);
      }
    },
    [company],
  );

  return { loading, error, saving, suppliers, withheld, reload: load, save };
}
